import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import authService from '../shared/services/authService';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const SUPPORTED_EMAIL_LANGUAGES: Language[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇬🇧',
  },
  {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷',
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
  },
  {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    flag: '🇵🇹',
  },
];

const LanguagePreferenceSectionSimple: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const [emailLanguage, setEmailLanguage] = useState(user?.preferred_language || 'en');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.preferred_language) {
      setEmailLanguage(user.preferred_language);
    }
  }, [user?.preferred_language]);

  const handleEmailLanguageChange = (event: SelectChangeEvent<string>) => {
    setEmailLanguage(event.target.value);
    setError(null);
    setSuccessMessage(null);
  };

  const handleSavePreferences = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    console.log('Saving language preference:', emailLanguage);

    try {
      // Update email language preference in backend
      const response = await authService.updateLanguagePreference(emailLanguage);

      console.log('Language update response:', response);

      if (response.success) {
        // Update user in store
        updateUser({
          ...user,
          preferred_language: emailLanguage,
        });

        setSuccessMessage('Email language preference updated successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        console.error('Language update failed:', response);
        throw new Error(response.message || 'Failed to update language preference');
      }
    } catch (err: any) {
      console.error('Error updating language preference:', err);

      // More detailed error message
      let errorMessage = 'Failed to update language preference';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = emailLanguage !== user?.preferred_language;
  const currentLanguage = SUPPORTED_EMAIL_LANGUAGES.find((lang) => lang.code === emailLanguage);

  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={3}>
          <LanguageIcon color="primary" sx={{ mr: 2 }} />
          <Typography variant="h6" component="h2">
            Email Language Preference
          </Typography>
          <Tooltip title="Choose the language for all email communications">
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            icon={<CheckIcon />}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Email Language Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              <EmailIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Select Your Preferred Email Language
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <InputLabel id="email-language-label">Email Language</InputLabel>
              <Select
                labelId="email-language-label"
                id="email-language"
                value={emailLanguage}
                onChange={handleEmailLanguageChange}
                label="Email Language"
                disabled={isLoading}
              >
                {SUPPORTED_EMAIL_LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Typography sx={{ mr: 1 }}>{lang.flag}</Typography>
                      <Typography sx={{ flexGrow: 1 }}>
                        {lang.name} ({lang.nativeName})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              All system emails including notifications, confirmations, and alerts will be sent in this language
            </Typography>
          </Box>

          {/* Current Language Info */}
          {currentLanguage && (
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Currently Selected
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h4">{currentLanguage.flag}</Typography>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {currentLanguage.name} ({currentLanguage.nativeName})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Your emails will be sent in {currentLanguage.name}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Info Box */}
          <Box sx={{ bgcolor: 'info.lighter', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <InfoIcon color="info" fontSize="small" sx={{ mt: 0.5 }} />
              <Box flex={1}>
                <Typography variant="body2" color="text.primary">
                  <strong>Note:</strong> This setting only affects email communications. The application interface language can be changed separately from the main menu.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Your language was initially detected based on your location. You can change it anytime.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Save Button */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Default language for all users: English
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setEmailLanguage(user?.preferred_language || 'en')}
                disabled={!hasChanges || isLoading}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePreferences}
                disabled={!hasChanges || isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {isLoading ? 'Saving...' : 'Save Preference'}
              </Button>
            </Box>
          </Box>

          {/* Supported Languages Info */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Available Languages
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {SUPPORTED_EMAIL_LANGUAGES.map((lang) => (
                <Chip
                  key={lang.code}
                  label={`${lang.flag} ${lang.name}`}
                  size="small"
                  variant={lang.code === emailLanguage ? 'filled' : 'outlined'}
                  color={lang.code === emailLanguage ? 'primary' : 'default'}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LanguagePreferenceSectionSimple;